import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import founderImage from "../assets/images/Group.png";
import { motion } from "framer-motion";

// Consistent Theme Styles
const customStyles = {
    neonGradientText: {
        background: "linear-gradient(90deg, #00F5A0, #00CFFF)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
    },
    ctaGradient: {
        background: "linear-gradient(to right, #FF2D55, #FF005C)",
    },
    glassCard: {
        background: "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(0, 245, 160, 0.2)",
    }
};

/* ---------- Animated Stat ---------- */
const AnimatedStatBox = ({ value, label, delay = 0 }) => {
    const [count, setCount] = useState(0);
    const numeric = parseInt(value.replace(/\D/g, ""));

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay }}
            onViewportEnter={() => {
                let start = 0;
                const duration = 2000; // 2 seconds
                const startTime = performance.now();
                
                const step = (now) => {
                    const elapsed = now - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    setCount(Math.floor(progress * numeric));
                    if (progress < 1) requestAnimationFrame(step);
                    else setCount(numeric);
                };
                requestAnimationFrame(step);
            }}
            className="rounded-2xl p-8 text-center"
            style={customStyles.glassCard}
        >
            <div className="text-4xl md:text-5xl font-extrabold mb-2" style={customStyles.neonGradientText}>
                {count}{value.includes('+') ? '+' : ''}
            </div>
            <div className="text-sm uppercase tracking-widest text-white/60 font-medium">{label}</div>
        </motion.div>
    );
};

/* ---------- Page ---------- */
export default function AboutUs() {
    return (
        <div className="min-h-screen flex flex-col text-white bg-[#0B0E11] overflow-hidden">
            <Navbar />

            <div className="flex-1 px-4 md:px-20 py-20 relative">
                {/* ===== Heading ===== */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-24"
                >
                    <p className="text-sm text-[#00F5A0] font-bold tracking-[0.3em] uppercase mb-4">
                        Discover Our Journey
                    </p>
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight">
                        <span className="text-white">THE </span>
                        <span style={customStyles.neonGradientText}>CRAFT TECH</span>
                        <br />
                        <span className="text-white">STORY</span>
                    </h1>
                    <div className="mx-auto mt-8 w-24 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, #00F5A0, #00CFFF)' }} />
                </motion.div>

                {/* ===== Stats ===== */}
                <section className="max-w-6xl mx-auto mb-32 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <AnimatedStatBox value="2020" label="Launched" delay={0.1} />
                        <AnimatedStatBox value="30+" label="Experts" delay={0.3} />
                        <AnimatedStatBox value="15+" label="Global Regions" delay={0.5} />
                    </div>
                </section>

                {/* ===== Founder Message ===== */}
                <motion.section
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="max-w-6xl mx-auto mb-32 rounded-[2rem] p-10 md:p-16 flex flex-col md:flex-row items-center gap-12"
                    style={customStyles.glassCard}
                >
                    <div className="relative">
                        <div className="absolute -inset-4 rounded-full blur-xl opacity-20" style={{ background: '#00F5A0' }}></div>
                        <img
                            src={founderImage}
                            alt="Founder"
                            className="relative w-56 h-56 md:w-72 md:h-72 object-cover rounded-2xl border-2 border-white/10"
                        />
                    </div>

                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6" style={customStyles.neonGradientText}>
                            Vision: Beyond the Horizon
                        </h2>

                        <blockquote className="border-l-4 pl-6 text-xl text-white/80 italic leading-relaxed mb-6"
                            style={{ borderColor: '#00F5A0' }}>
                            “Craft Tech was founded with one belief — technology should not just
                            support businesses, it should accelerate them. We don’t follow
                            trends, we engineer the future.”
                        </blockquote>

                        <p className="text-lg font-bold tracking-wide uppercase text-white">
                            — Founder, <span style={customStyles.neonGradientText}>Craft Tech</span>
                        </p>
                    </div>
                </motion.section>

                {/* ===== Core Philosophy ===== */}
                <section className="max-w-6xl mx-auto mb-32">
                    <motion.h2 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-center text-4xl font-extrabold mb-16 text-white"
                    >
                        Our Core <span style={customStyles.neonGradientText}>Philosophy</span>
                    </motion.h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <InfoCard title="Mission" icon="🚀" text="Engineer scalable digital systems that drive measurable growth and industry-leading performance." delay={0.2} />
                        <InfoCard title="Vision" icon="💡" text="To be a globally trusted technology partner for modern businesses seeking excellence." delay={0.4} />
                        <InfoCard title="Values" icon="🛠️" text="Integrity, innovation, and a relentless focus on our clients' long-term success." delay={0.6} />
                    </div>
                </section>

                {/* ===== CTA ===== */}
                <motion.section 
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center py-10"
                >
                    <h2 className="text-4xl md:text-5xl font-extrabold mb-6">
                        Let’s Build the <span style={customStyles.neonGradientText}>Future</span> Together
                    </h2>
                    <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
                        Ready to turn your idea into a high-impact digital product with the Craft Tech expertise?
                    </p>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="rounded-full px-12 py-4 font-bold text-white tracking-widest uppercase shadow-2xl"
                        style={customStyles.ctaGradient}
                    >
                        Start a Project →
                    </motion.button>
                </motion.section>
            </div>

            <Footer />
        </div>
    );
}

/* ---------- Helper Card (Glassmorphism) ---------- */
const InfoCard = ({ title, icon, text, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        whileHover={{ y: -10 }}
        className="rounded-3xl p-10 text-center transition-all duration-300"
        style={customStyles.glassCard}
    >
        <div className="text-5xl mb-6 drop-shadow-lg">{icon}</div>
        <h3 className="text-2xl font-bold mb-4" style={customStyles.neonGradientText}>{title}</h3>
        <p className="text-white/60 leading-relaxed">{text}</p>
    </motion.div>
);